import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  MapPin, Plus, Edit, Trash2, Loader2, Building2, Phone, Mail,
  Search, ChevronsUpDown, Star, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Combobox inline para valências (igual ao InstituicoesPage)
function ValenciaComboboxInline({ value, onChange, valencias }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = !search.trim()
    ? valencias
    : valencias.filter(v => v.nome.toLowerCase().includes(search.toLowerCase()) || v.categoria?.toLowerCase().includes(search.toLowerCase()));
  const selected = valencias.find(v => v.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal text-left h-9">
          <span className="truncate text-sm">{selected ? selected.nome : 'Selecione resposta social...'}</span>
          <ChevronsUpDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <div className="flex items-center border rounded-md px-3 mb-2 bg-white dark:bg-slate-800">
          <Search className="w-4 h-4 mr-2 text-slate-400 shrink-0" />
          <input autoFocus className="flex-1 py-1.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
            placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {filtered.map(v => (
            <button key={v.id} type="button" onClick={() => { onChange(v.id); setOpen(false); setSearch(''); }}
              className={`w-full text-left px-3 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-between ${value === v.id ? 'bg-[#0066CC]/10 text-[#0066CC]' : 'text-slate-800 dark:text-slate-200'}`}>
              <span>{v.nome}</span>
              <span className="text-[10px] text-slate-400 ml-2">{v.categoria}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MoradaForm({ morada = {}, valencias, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    designacao: morada.designacao || '',
    morada: morada.morada || '',
    codigo_postal: morada.codigo_postal || '',
    localidade: morada.localidade || '',
    distrito: morada.distrito || '',
    concelho: morada.concelho || '',
    telefone: morada.telefone || '',
    email: morada.email || '',
    principal: morada.principal || false,
    valencias: morada.valencias || [],
    observacoes: morada.observacoes || ''
  });
  const [cpLoading, setCpLoading] = useState(false);

  const handleCPLookup = async (cp) => {
    const clean = cp.trim();
    if (clean.length < 4) return;
    setCpLoading(true);
    try {
      const resp = await apiClient.get(`/codigos-postais/smart-lookup?codigo=${encodeURIComponent(clean)}`);
      const data = resp.data;
      if (data?.found) {
        setForm(f => ({
          ...f,
          codigo_postal: clean,
          localidade: data.localidade || f.localidade,
          concelho: data.concelho || f.concelho,
          distrito: data.distrito || f.distrito
        }));
        const parts = [data.localidade, data.concelho, data.distrito].filter(Boolean);
        toast.success(`Localização: ${parts.join(', ')}`);
      } else {
        toast.info('Código postal não encontrado — preencha manualmente.');
      }
    } catch (e) {
      toast.info('Não foi possível obter dados do código postal.');
    } finally {
      setCpLoading(false);
    }
  };

  const addValencia = (valencia_id) => {
    const v = valencias.find(x => x.id === valencia_id);
    if (!v || form.valencias.find(x => x.valencia_id === valencia_id)) return;
    setForm(f => ({ ...f, valencias: [...f.valencias, { valencia_id, valencia_nome: v.nome, numero_utentes: 0 }] }));
  };

  const removeValencia = (valencia_id) => {
    setForm(f => ({ ...f, valencias: f.valencias.filter(x => x.valencia_id !== valencia_id) }));
  };

  const updateUtentes = (valencia_id, value) => {
    setForm(f => ({ ...f, valencias: f.valencias.map(x => x.valencia_id === valencia_id ? { ...x, numero_utentes: parseInt(value) || 0 } : x) }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Designação (ex: Sede, Polo Norte)</Label>
          <Input value={form.designacao} onChange={e => setForm(f => ({ ...f, designacao: e.target.value }))} placeholder="Designação desta morada" className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Morada *</Label>
          <Input value={form.morada} onChange={e => setForm(f => ({ ...f, morada: e.target.value }))} placeholder="Rua, número..." className="h-8 text-sm" required />
        </div>
        <div>
          <Label className="text-xs">Código Postal</Label>
          <div className="relative">
            <Input
              value={form.codigo_postal}
              onChange={e => setForm(f => ({ ...f, codigo_postal: e.target.value }))}
              onBlur={e => handleCPLookup(e.target.value)}
              placeholder="0000-000"
              className="h-8 text-sm pr-8"
              data-testid="input-cp-morada"
            />
            {cpLoading && (
              <Loader2 className="absolute right-2 top-1.5 w-4 h-4 animate-spin text-slate-400" />
            )}
          </div>
        </div>
        <div>
          <Label className="text-xs">Localidade</Label>
          <Input value={form.localidade} onChange={e => setForm(f => ({ ...f, localidade: e.target.value }))} placeholder="Localidade" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Concelho</Label>
          <Input value={form.concelho} onChange={e => setForm(f => ({ ...f, concelho: e.target.value }))} placeholder="Concelho" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Distrito</Label>
          <Input value={form.distrito} onChange={e => setForm(f => ({ ...f, distrito: e.target.value }))} placeholder="Distrito" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Telefone</Label>
          <Input value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} placeholder="Telefone" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@morada.pt" className="h-8 text-sm" />
        </div>
      </div>

      {/* Valências associadas */}
      <div>
        <Label className="text-xs font-semibold">Respostas Sociais / Valências desta morada</Label>
        <div className="mt-1">
          <ValenciaComboboxInline
            value=""
            onChange={addValencia}
            valencias={valencias.filter(v => !form.valencias.find(x => x.valencia_id === v.id))}
          />
        </div>
        {form.valencias.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {form.valencias.map(v => (
              <div key={v.valencia_id} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded border text-sm">
                <span className="flex-1 truncate">{v.valencia_nome}</span>
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} value={v.numero_utentes}
                    onChange={e => updateUtentes(v.valencia_id, e.target.value)}
                    className="w-16 h-6 text-xs text-right" title="Nº utentes" />
                  <span className="text-xs text-slate-400">ut.</span>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => removeValencia(v.valencia_id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs">Observações</Label>
        <Textarea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} rows={2} className="text-sm" />
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" id="principal" checked={form.principal} onChange={e => setForm(f => ({ ...f, principal: e.target.checked }))} className="rounded" />
        <Label htmlFor="principal" className="text-sm cursor-pointer">Morada principal</Label>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={() => onSave(form)} disabled={saving || !form.morada.trim()}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
      </DialogFooter>
    </div>
  );
}

export function MoradasSection({ instituicaoId, valencias: valenciasProp = [] }) {
  const [moradas, setMoradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMorada, setEditingMorada] = useState(null);
  const [saving, setSaving] = useState(false);
  // Carregar valências internamente (não depender do pai)
  const [valencias, setValencias] = useState(valenciasProp);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    loadMoradas();
  }, [instituicaoId]);

  // Carregar lista de valências disponíveis se não foram passadas
  useEffect(() => {
    if (valenciasProp.length > 0) {
      setValencias(valenciasProp);
    } else {
      apiClient.get('/valencias').then(r => {
        if (r.data && Array.isArray(r.data)) setValencias(r.data);
      }).catch(() => {});
    }
  }, [instituicaoId]);

  const loadMoradas = async () => {
    try {
      setLoading(true);
      const resp = await fetch(`${API}/api/instituicoes/${instituicaoId}/moradas`, {
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      if (resp.ok) setMoradas(await resp.json());
    } catch (e) { /* silencioso */ }
    finally { setLoading(false); }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const url = editingMorada
        ? `${API}/api/instituicoes/${instituicaoId}/moradas/${editingMorada.id}`
        : `${API}/api/instituicoes/${instituicaoId}/moradas`;
      const resp = await fetch(url, {
        method: editingMorada ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify(form)
      });
      if (!resp.ok) throw new Error('Erro ao guardar');
      toast.success(editingMorada ? 'Morada atualizada' : 'Morada adicionada');
      setModalOpen(false);
      setEditingMorada(null);
      loadMoradas();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (moradaId) => {
    if (!window.confirm('Remover esta morada?')) return;
    try {
      await fetch(`${API}/api/instituicoes/${instituicaoId}/moradas/${moradaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      toast.success('Morada removida');
      loadMoradas();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  // Total de valências únicas em todas as moradas
  const totalValencias = [...new Set(moradas.flatMap(m => m.valencias?.map(v => v.valencia_id) || []))].length;
  const totalUtentes = moradas.reduce((sum, m) => sum + (m.valencias?.reduce((s, v) => s + (v.numero_utentes || 0), 0) || 0), 0);

  return (
    <Card data-testid="card-moradas">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#0066CC]" />
            Moradas e Respostas Sociais
          </div>
          <div className="flex items-center gap-3">
            {moradas.length > 0 && (
              <div className="flex gap-2 text-xs text-slate-500">
                <span>{moradas.length} morada(s)</span>
                {totalValencias > 0 && <span>• {totalValencias} resposta(s) social</span>}
                {totalUtentes > 0 && <span>• {totalUtentes} utentes</span>}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => { setEditingMorada(null); setModalOpen(true); }}
              data-testid="btn-add-morada">
              <Plus className="w-4 h-4 mr-1" />Nova morada
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">A carregar...</span></div>
        ) : moradas.length === 0 ? (
          <div className="text-center py-6">
            <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Sem moradas adicionais registadas</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => { setEditingMorada(null); setModalOpen(true); }}>
              <Plus className="w-4 h-4 mr-1" />Adicionar primeira morada
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {moradas.map(m => (
              <div key={m.id} data-testid={`morada-item-${m.id}`}
                className="border rounded-lg p-3 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm dark:text-white truncate">
                        {m.designacao || m.morada}
                      </span>
                      {m.principal && (
                        <Badge className="bg-[#0066CC] text-[10px] px-1.5 py-0 flex items-center gap-0.5">
                          <Star className="w-2.5 h-2.5" />Principal
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{m.morada}{m.localidade ? `, ${m.localidade}` : ''}{m.codigo_postal ? ` — ${m.codigo_postal}` : ''}</p>
                    {(m.telefone || m.email) && (
                      <div className="flex items-center gap-3 mt-1">
                        {m.telefone && <span className="text-xs text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" />{m.telefone}</span>}
                        {m.email && <span className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>}
                      </div>
                    )}
                    {m.valencias?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {m.valencias.map(v => {
                          const masculino = v.utentes_masculino || 0;
                          const feminino = v.utentes_feminino || 0;
                          const total = v.numero_utentes || (masculino + feminino);
                          const hasDemografia = masculino > 0 || feminino > 0;
                          return hasDemografia ? (
                            <div key={v.valencia_id} className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-full text-[11px]">
                              <span className="font-medium text-slate-700 dark:text-slate-200">{v.valencia_nome}</span>
                              <span className="text-blue-600 dark:text-blue-400">{masculino}M</span>
                              <span className="text-pink-500">+</span>
                              <span className="text-pink-600 dark:text-pink-400">{feminino}F</span>
                              <span className="text-slate-500 font-semibold">={total}</span>
                            </div>
                          ) : (
                            <Badge key={v.valencia_id} variant="outline" className="text-[10px] px-1.5 py-0">
                              {v.valencia_nome}{total > 0 ? ` (${total})` : ''}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => { setEditingMorada(m); setModalOpen(true); }}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(m.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={(v) => { setModalOpen(v); if (!v) setEditingMorada(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMorada ? 'Editar Morada' : 'Nova Morada'}</DialogTitle>
          </DialogHeader>
          <MoradaForm
            morada={editingMorada || {}}
            valencias={valencias}
            onSave={handleSave}
            onCancel={() => { setModalOpen(false); setEditingMorada(null); }}
            saving={saving}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
